import React from 'react';
import { emptyArray } from 'ui-components/default-props/empty';
import { get } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import { UserDateFormat } from '../../components/user-date-format/index';
import { IntlUnitFormattingService } from '../../services/intl-unit-formatting-services/intl-unit-formatting-service';
import { convertPackageWeightAndDimensions } from '../../services/unit-services/package-services/package-unit-conversion-service';
import { ShipmentHelperService } from '../../services/shipment-helper-service';
import { FormattedCurrencyContainer } from '../formatted-currency/container';

import { DimensionUnit } from '../../enums/dimension-unit';
import { messages } from './messages';

export const getUnitAbbreviation = (dimensionsUnit) => {
  if (dimensionsUnit === DimensionUnit.Centimeters) {
    return <FormattedMessage {...messages.centimetersAbbreviation} />;
  }
  return <FormattedMessage {...messages.inchesAbbreviation} />;
};

export const formatDimensions = (dimensions) => {
  const { length, width, height, unit } = dimensions || {};

  // todo this probably should be using the Unit Formatting Service
  if (!dimensions || length === undefined || width === undefined || height === undefined) {
    return undefined;
  }
  return (
    <FormattedMessage
      {...messages.dimensions}
      values={{
        length,
        width,
        height,
        unit: getUnitAbbreviation(unit),
      }}
    />
  );
};

export const formatCurrency = (cost) => {
  // TODO: this needs to use the default currency when that is added in the internationalization effort.
  const value = get(cost, 'value', undefined);
  if (value === undefined) {
    return undefined;
  }
  const code = get(cost, 'code', 'USD');
  return <FormattedCurrencyContainer currencyCode={code} value={value} />;
};

export const formatWeight = (weight, weightUnit) => {
  if (weight === undefined || weight === null || weight.value === undefined || weight.unit === undefined) {
    return undefined;
  }
  return IntlUnitFormattingService.formatWeight(weight, weightUnit);
};

const packagesInsuredValue = (packages) => {
  if (!packages || packages.length === 0) {
    return undefined;
  }
  const totalInsuredValue = packages.reduce((total, packageData) => {
    return total + get(packageData, 'insuredValue.value', 0);
  }, 0);
  const firstCurrencyCode = get(packages, '0.insuredValue.code', 'USD');
  return {
    value: totalInsuredValue,
    code: firstCurrencyCode,
  };
};

const getDetailsFromFulfillment = (fulfillment, dimensionsUnit, weightUnit) => {
  const { dimensions, weight } = convertPackageWeightAndDimensions(
    {
      dimensions: fulfillment.dimensions,
      weight: fulfillment.weight,
    },
    weightUnit,
    dimensionsUnit,
  );
  return {
    cost: formatCurrency(ShipmentHelperService.getTotalCost(fulfillment)),
    expectedDelivery: fulfillment.expectedDeliveryDateTime ? <UserDateFormat value={fulfillment.expectedDeliveryDateTime} /> : undefined,
    dimensions: formatDimensions(dimensions),
    weight: formatWeight(weight, weightUnit),
    dimensionsUnit,
  };
};

const getDetailsFromFulfillmentPlan = (fulfillmentPlan, dimensionsUnit, weightUnit) => {
  const pack = get(fulfillmentPlan, 'packages.0');
  const { dimensions, weight } = convertPackageWeightAndDimensions(pack, weightUnit, dimensionsUnit);
  return {
    cost: formatCurrency(get(fulfillmentPlan, 'rateSummary.rate.totalCost')),
    dimensions: formatDimensions(dimensions),
    weight: formatWeight(weight, weightUnit),
    expectedDelivery: undefined,
    dimensionsUnit,
  };
};

export const shipmentPackagingDetails = (fulfillmentPlan, fulfillment, shipFrom, packageType, dimensionsUnit, weightUnit) => {
  const hasFulfillment = !!get(fulfillment, 'fulfillmentId');
  // TODO: if these currency codes could ever be different from package to package this total may not be accurate
  const insurance = formatCurrency(packagesInsuredValue(get(fulfillmentPlan, 'packages', emptyArray)));
  const confirmation = get(fulfillmentPlan, 'options.confirmation');
  const shipDateTime = get(fulfillmentPlan, 'shipDateTime');
  const dateShipped = shipDateTime ? <UserDateFormat value={shipDateTime} /> : undefined;
  if (hasFulfillment) {
    return {
      ...getDetailsFromFulfillment(fulfillment, dimensionsUnit, weightUnit),
      insurance,
      confirmation,
      dateShipped,
      shipFrom,
      packageType,
    };
  }
  return {
    ...getDetailsFromFulfillmentPlan(fulfillmentPlan, dimensionsUnit, weightUnit),
    insurance,
    confirmation,
    dateShipped,
    shipFrom,
    packageType,
  };
};

export const returnShipmentPackagingDetails = (returnShipment, shipTo, packageType, dimensionsUnit, weightUnit) => {
  const insurance = formatCurrency(packagesInsuredValue(get(returnShipment, 'returnConfiguration.packages', emptyArray)));
  const shipDate = get(returnShipment, 'trackingInformation.shipDate');
  const deliveryDate = get(returnShipment, 'trackingInformation.deliveryDate');
  const dateShipped = shipDate ? <UserDateFormat value={shipDate} /> : undefined;
  const expectedDelivery = dateShipped ? <UserDateFormat value={deliveryDate} /> : undefined;
  const pack = get(returnShipment, 'returnConfiguration.packages[0]');
  const convertedPackage = convertPackageWeightAndDimensions(pack, weightUnit, dimensionsUnit);
  const weight = formatWeight(convertedPackage.weight, weightUnit);
  const confirmation = get(returnShipment, 'returnConfiguration.options.confirmation');
  const dimensions = formatDimensions(convertedPackage.dimensions);

  return {
    insurance,
    expectedDelivery,
    weight,
    confirmation,
    dateShipped,
    shipTo,
    packageType,
    dimensions,
    dimensionsUnit,
  };
};

export const thirdPartyFulfillmentPackagingDetails = (fulfillmentPlan, fulfillment, shipFrom) => {
  const shipDateTime = get(fulfillmentPlan, 'shipDateTime');
  const dateShipped = shipDateTime ? <UserDateFormat value={shipDateTime} /> : undefined;
  const expectedDelivery = get(fulfillment, 'expectedDeliveryDateTime');
  const formattedExpectedDelivery = expectedDelivery ? <UserDateFormat value={expectedDelivery} /> : undefined;
  return {
    shipFrom,
    expectedDelivery: formattedExpectedDelivery,
    dateShipped,
  };
};
